// src/admin/ManageProfessionals.js

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../config/firebaseConfig';
import { collection, getDocs, doc, deleteDoc, updateDoc, query, where } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import EditProfessionalModal from './EditProfessionalModal';

const ManageProfessionals = () => {
    // State'leri bekleyen ve onaylananlar olarak ayırıyoruz
    const [pendingProfessionals, setPendingProfessionals] = useState([]);
    const [approvedProfessionals, setApprovedProfessionals] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' veya 'approved'

    // Modal state'leri
    const [showModal, setShowModal] = useState(false);
    const [selectedProfessional, setSelectedProfessional] = useState(null);

    const categories = ["All", "Agent", "Authors", "Distributors", "Editors", "Illustrators", "Marketing", "Platforms", "Printers", "Publishers", "Stockists"].sort();

    // Veri çekme fonksiyonunu useCallback ile optimize ediyoruz
    const fetchProfessionals = useCallback(async () => {
        setLoading(true);
        try {
            // Onay bekleyenleri çek ('pending')
            const pendingQuery = query(collection(db, 'professionals'), where('status', '==', 'pending'));
            const pendingSnapshot = await getDocs(pendingQuery);
            const pendingList = pendingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPendingProfessionals(pendingList);

            // Onaylanmış olanları çek ('approved')
            const approvedQuery = query(collection(db, 'professionals'), where('status', '==', 'approved'));
            const approvedSnapshot = await getDocs(approvedQuery);
            const approvedList = approvedSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setApprovedProfessionals(approvedList);

        } catch (err) {
            console.error(err);
            setError('Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfessionals();
    }, [fetchProfessionals]);

    // Filtreleme mantığını aktif sekmeye göre ayırıyoruz
    const filteredData = useMemo(() => {
        const data = activeTab === 'pending' ? pendingProfessionals : approvedProfessionals;
        if (filterCategory === 'All') {
            return data;
        }
        return data.filter(p => p.category === filterCategory);
    }, [pendingProfessionals, approvedProfessionals, filterCategory, activeTab]);

    // YENİ: Onaylama Fonksiyonu
    const handleApprove = async (id) => {
        if (window.confirm('Are you sure you want to approve this submission?')) {
            try {
                const professionalRef = doc(db, 'professionals', id);
                await updateDoc(professionalRef, {
                    status: 'approved'
                });
                alert('Submission approved and is now live!');
                fetchProfessionals(); // Listeleri yenile
            } catch (error) {
                alert('Failed to approve the submission.');
                console.error(error);
            }
        }
    };
    
    // handleDelete fonksiyonu aynı kalabilir
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to permanently delete this entry?')) {
            try {
                await deleteDoc(doc(db, 'professionals', id));
                alert('Entry deleted successfully!');
                fetchProfessionals(); // Listeleri yenile
            } catch (err) {
                alert('Failed to delete the entry.');
            }
        }
    };

    const handleEdit = (professional) => {
        setSelectedProfessional(professional);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedProfessional(null);
    };
    
    // handleExportToExcel fonksiyonu aynı kalabilir
    const handleExportToExcel = () => {
        const dataToExport = filteredData.map(({ id, createdAt, photoURL, consentGiven, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Professionals");
        XLSX.writeFile(workbook, `Professionals_${activeTab}.xlsx`);
    };

    if (loading) return <div className="container mt-5 text-center">Loading submissions...</div>;
    if (error) return <div className="container mt-5 alert alert-danger">{error}</div>;

    return (
        <div className="container mt-5">
            <h2>Manage Directory Submissions</h2>
            <p>Approve new submissions or manage existing live entries.</p>

            {/* SEKMELİ YAPI */}
            <ul className="nav nav-tabs my-4">
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
                        Pending Approval <span className="badge bg-warning ms-1">{pendingProfessionals.length}</span>
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'approved' ? 'active' : ''}`} onClick={() => setActiveTab('approved')}>
                        Manage Approved <span className="badge bg-success ms-1">{approvedProfessionals.length}</span>
                    </button>
                </li>
            </ul>

            <div className="d-flex justify-content-between align-items-center my-3">
                <div className="w-50">
                    <select className="form-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>
                <button className="btn btn-success" onClick={handleExportToExcel}>
                    <i className="bi bi-file-earmark-excel me-2"></i>Export to Excel
                </button>
            </div>
            
            <div className="table-responsive">
                <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                        <tr>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((prof) => (
                            <tr key={prof.id}>
                                <td>{prof.name}</td>
                                <td>{prof.category}</td>
                                <td>{prof.email || '-'}</td>
                                <td>
                                    <span className={`badge ${prof.status === 'approved' ? 'bg-success' : 'bg-warning'}`}>
                                        {prof.status}
                                    </span>
                                </td>
                                <td>
                                    {activeTab === 'pending' && (
                                        <button className="btn btn-success btn-sm me-2" onClick={() => handleApprove(prof.id)}>Approve</button>
                                    )}
                                    <button className="btn btn-warning btn-sm me-2" onClick={() => handleEdit(prof)}>Edit</button>
                                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(prof.id)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                 {filteredData.length === 0 && <p className="text-center text-muted mt-4">No entries found in this category.</p>}
            </div>

            {selectedProfessional && (
                <EditProfessionalModal
                    show={showModal}
                    handleClose={handleCloseModal}
                    professional={selectedProfessional}
                    onDataUpdate={fetchProfessionals} // Listeyi güncellemesi için ana fonksiyonu veriyoruz
                />
            )}
        </div>
    );
};

export default ManageProfessionals;